import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiCalendar, FiEdit, FiTrash2 } from "react-icons/fi";
import { SchedulingInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import InfoBanner from "../../components/InfoBanner";
import {
  SchedulingStatusLabel,
  TypeSchedulingStatus
} from "../../components/enum/schedulingStatusEnum";
import HelpVideoButton from "../../components/HelpVideoButton";
import SchedulingCreateModal from "../../components/modal/SchedulingCreateModal";
import PaginationControls from "../../components/PaginationControls";
import StatusSwitch from "../../components/switch/StatusSwitch";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  Container,
  SchedulingSearchCard,
  StatusCell,
  StatusMyNotify,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "./scheduling.styled";

export const SchedulingPage = () => {
  const [search, setSearch] = useState("");

  const [schedules, setSchedules] = useState<SchedulingInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Controle do diálogo de confirmação de exclusão
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<number | null>(null);
  const [scheduleClientName, setScheduleClientName] = useState<string | null>(
    null
  );

  // **Controle do modal de criação/edição**
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedScheduling, setSelectedScheduling] =
    useState<SchedulingInterface | null>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [statusValue, setStatusValue] = useState<TypeSchedulingStatus>(
    TypeSchedulingStatus.NONE
  );

  const containerId = "toaster-scheduling-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const { isAdmin, isUser } = useAuth();

  // Busca agendamentos paginados
  const fetchSchedules = async (currentPage: number) => {
    try {
      const response = await api.get(`/scheduling`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          scheduling_status:
            statusValue !== TypeSchedulingStatus.NONE ? statusValue : undefined
        }
      });

      setSchedules(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar agendamentos:", error);
      autoError(msg);
    }
  };

  // Abre diálogo de confirmação antes de excluir agendamento
  const openDeleteDialog = (sched: SchedulingInterface) => {
    setScheduleToDelete(sched.scheduling_id);
    setScheduleClientName(sched.client?.client_name ?? "(sem cliente)");
    setDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setScheduleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (scheduleToDelete !== null) {
      try {
        await api.delete(`/scheduling/${scheduleToDelete}`);
        autoSuccess(`Agendamento removido com sucesso!`);
        fetchSchedules(page);
      } catch (error) {
        const msg = extractErrorMessage(error);
        console.error("Erro ao deletar agendamento:", error);
        autoError(msg);
      } finally {
        setDialogOpen(false);
        setScheduleToDelete(null);
      }
    }
  };

  // ***********************
  // 1) Criar um handle que abre o modal em modo “novo agendamento”
  // ***********************
  const handleOpenCreateModal = () => {
    setSelectedScheduling(null); // garante que o modal abra em branco
    setIsModalOpen(true);
  };

  // ***********************
  // 2) Criar um handle que abre o modal em modo “edição”, já carregando os dados
  // ***********************
  const handleOpenEditModal = (sched: SchedulingInterface) => {
    setSelectedScheduling(sched);
    setIsModalOpen(true);
  };

  // ***********************
  // 3) Recarrega lista e fecha modal quando ele for fechado
  // ***********************
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedScheduling(null);
    fetchSchedules(page);
  };

  const handleToggleStatus = async (schedule: SchedulingInterface) => {
    try {
      let current_status = !schedule.notify_active;
      await api.patch(`/scheduling/${schedule.scheduling_id}`, {});

      if (current_status) {
        autoSuccess("Notificação ativada com sucesso!");
      } else {
        autoWarn("Notificação desativada com sucesso!");
      }

      fetchSchedules(page); // Atualiza a lista após o toggle
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao atualizar status do cliente:", error);
      autoError(msg);
    }
  };

  useEffect(() => {
    fetchSchedules(page);
  }, [page, search, startDate, endDate, statusValue]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Agendamentos
        <CreateButton
          onClick={handleOpenCreateModal}
          style={{ marginLeft: "14px" }}
        >
          <FiCalendar size={18} /> Criar Agendamento
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/zn6SYIp1MEY"
          modalTitle="Gerenciar Agendamentos"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <InfoBanner icon={<FiCalendar size={26} />} title="Agendamentos">
        Aqui você marca visitas e compromissos com um cliente específico, com
        data e hora definidas — diferente de Pendências, que são lembretes
        soltos sem cliente vinculado.
      </InfoBanner>

      <SchedulingSearchCard>
        <TextField
          label="Data de Início"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras no fuso local
          }}
        />

        <TextField
          disabled={!startDate}
          label="Data Final"
          type="date"
          value={startDate ? endDate : ""}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || undefined, // impede menor que startDate
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras
          }}
        />

        <TextFeldClientSearch
          id="scheduling_search"
          label="Dados do Agendamento"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reseta para primeira página ao buscar
          }}
          style={{ marginBottom: "16px", minWidth: "300px" }}
        />

        <FormControl style={{ minWidth: 200 }}>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            labelId="label-status"
            value={statusValue}
            label="Status"
            onChange={(e) =>
              setStatusValue(e.target.value as TypeSchedulingStatus)
            }
          >
            {(Object.values(TypeSchedulingStatus) as TypeSchedulingStatus[])
              .filter((code) => code !== TypeSchedulingStatus.NONE)
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {SchedulingStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </SchedulingSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data / Hora</th>
              <th>Descrição</th>
              <th>Status</th>
              <th>Cliente</th>
              <th>Telefone</th>
              {isAdmin && <th>Empresa</th>}
              <th>Usuário</th>
              <th>Notificação</th>
              {!isUser && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {schedules.map((sched) => {
              // Formata data e hora para pt-BR
              const dateObj = new Date(sched.scheduling_date);
              const formattedDate = dateObj.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              });
              const formattedTime = dateObj.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              });

              return (
                <tr key={sched.scheduling_id}>
                  <td>{`${formattedDate} ${formattedTime}`}</td>
                  <td>{sched.scheduling_description}</td>
                  {/* <td>{sched.scheduling_status}</td> */}
                  <StatusCell status={sched.scheduling_status}>
                    {SchedulingStatusLabel[sched.scheduling_status]}
                  </StatusCell>
                  <td>{sched.client?.client_name ?? "(sem cliente)"}</td>
                  <td>{sched.client?.TB_PHONE?.phone_number ?? "-"}</td>
                  {isAdmin && <td>{sched.company.company_name}</td>}
                  <td>{sched.user.user_name}</td>
                  {isAdmin ? (
                    <td>
                      {" "}
                      <StatusSwitch
                        checked={sched.notify_active}
                        onToggle={() => handleToggleStatus(sched)}
                      />
                    </td>
                  ) : sched.notify_active ? (
                    <StatusMyNotify status={sched.notify_active}>
                      Ativo
                    </StatusMyNotify>
                  ) : (
                    <StatusMyNotify status={sched.notify_active}>
                      Inativo
                    </StatusMyNotify>
                  )}
                  {!isUser && (
                    <ActionsCell>
                      <CustomTooltip title="Editar Agendamento">
                        <ActionButton
                          onClick={() => handleOpenEditModal(sched)}
                        >
                          <FiEdit size={20} color="#4caf50" />
                        </ActionButton>
                      </CustomTooltip>

                      <CustomTooltip title="Excluir Agendamento">
                        <ActionButton onClick={() => openDeleteDialog(sched)}>
                          <FiTrash2 size={20} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </ActionsCell>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o agendamento do cliente{" "}
            <strong>{scheduleClientName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <SchedulingCreateModal
        open={isModalOpen}
        onClose={handleCloseModal}
        scheduling={selectedScheduling}
      />
    </Container>
  );
};
